import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Minimal data hook: `{ data, error, loading, reload }`.
 * No caching layer on purpose — one fetch per screen mount, refetch on demand.
 */
export function useApi(loader, deps = [], { skip = false } = {}) {
  const [state, setState] = useState({ data: null, error: null, loading: !skip });
  const [nonce, setNonce] = useState(0);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    if (skip) {
      setState({ data: null, error: null, loading: false });
      return undefined;
    }
    let active = true;
    setState((prev) => ({ data: prev.data, error: null, loading: true }));
    Promise.resolve()
      .then(() => loaderRef.current())
      .then((data) => {
        if (active) setState({ data, error: null, loading: false });
      })
      .catch((error) => {
        if (active) setState({ data: null, error, loading: false });
      });
    return () => {
      active = false;
    };
    // The caller owns the dependency list; `loader` is read through a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce, skip]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { ...state, reload };
}
